import { useEffect, useState } from 'react';
import axios from 'axios';
import Reveal from '../components/site/Reveal';
import { useT } from '../i18n/LanguageContext';
import { useExternalLink } from '../contexts/ExternalLinkContext';
import { Calendar, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function News() {
  const { t } = useT();
  const { confirm } = useExternalLink();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/news`).then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="page-news">
      <section className="pt-40 pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <p className="eyebrow mb-6">{t('news.eyebrow')}</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter text-prim max-w-4xl">
              {t('news.title')}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="py-16 border-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          {loading ? (
            <p className="text-sec font-mono text-sm">{t('common.loading')}</p>
          ) : items.length === 0 ? (
            <p className="text-sec">{t('news.empty')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((n, i) => (
                <Reveal key={n.id} delay={(i % 3) * 100}>
                  <article data-testid={`news-card-${n.id}`} className="border border-subtle surface-2 rounded-md card-glow overflow-hidden h-full flex flex-col">
                    <div className="aspect-[3/2] surface bg-gradient-to-br from-brand-maroon/10 to-brand-teal/10 relative overflow-hidden">
                      {n.cover_base64 ? (
                        <img src={n.cover_base64} alt={n.title} className="photo-fit" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="font-display text-6xl text-muted/30">N</span>
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 text-muted text-xs font-mono">
                        <Calendar size={12} />
                        <span>{n.date}</span>
                      </div>
                      <h3 className="mt-3 font-display text-xl text-prim tracking-tight leading-snug">{n.title}</h3>
                      {n.summary && <p className="mt-3 text-sec text-sm leading-relaxed flex-1">{n.summary}</p>}
                      {n.link && (
                        <a
                          href={n.link}
                          onClick={(e) => { e.preventDefault(); confirm(n.link, n.title); }}
                          className="mt-5 inline-flex items-center gap-1.5 text-brand-teal-light text-sm hover-line"
                        >
                          {t('common.readMore')} <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
